'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { persistMetaAttribution, trackMetaPixel } from '@/lib/meta-pixel';

const PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID_RUPIQA?.trim();

type QueuedPixel = ((...args: unknown[]) => void) & { queue: unknown[][] };

function isLoanContent(pathname: string) {
  return (
    pathname === '/' ||
    pathname === '/rupiqa' ||
    pathname.startsWith('/apply') ||
    pathname === '/rupiqa/apply' ||
    pathname.startsWith('/rupiqa/loans') ||
    pathname.startsWith('/loan') ||
    pathname.startsWith('/r/') ||
    pathname.startsWith('/o/')
  );
}

export default function MetaPixelTracker() {
  const pathname = usePathname() || '/';
  const privateJourney = pathname === '/rupiqa/customer' || pathname === '/rupiqa/partner/reset';
  const searchParams = useSearchParams();
  const search = searchParams?.toString() || '';

  useEffect(() => {
    if (privateJourney || pathname === '/rupiqa/partner/register' || pathname.startsWith('/rupiqa/p/')) return;
    // Activation needs a separate reviewed consent/event configuration.
    if (process.env.NEXT_PUBLIC_META_PIXEL_ENABLED_RUPIQA !== 'true' || !PIXEL_ID) return;
    persistMetaAttribution(search ? `?${search}` : '');

    if (!window.fbq) {
      const fbq = ((...args: unknown[]) => {
        fbq.queue.push(args);
      }) as QueuedPixel;
      fbq.queue = [];
      window.fbq = fbq;
      window._fbq = fbq;
    }

    if (!window.__rupiqaMetaPixelInitialized) {
      window.fbq('init', PIXEL_ID);
      window.__rupiqaMetaPixelInitialized = true;

      if (!document.getElementById('rupiqa-meta-pixel')) {
        const script = document.createElement('script');
        script.id = 'rupiqa-meta-pixel';
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        document.head.appendChild(script);
      }
    }

    trackMetaPixel('PageView');
    if (isLoanContent(pathname)) trackMetaPixel('ViewContent', { content_name: pathname });
  }, [pathname, search, privateJourney]);

  useEffect(() => {
    if (privateJourney || pathname === '/rupiqa/partner/register' || pathname.startsWith('/rupiqa/p/')) return;
    if (process.env.NEXT_PUBLIC_META_PIXEL_ENABLED_RUPIQA !== 'true' || !PIXEL_ID) return;

    const onClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target.closest('a, button') : null;
      if (!target) return;
      const href = target.getAttribute('href') || '';
      const label = (target.textContent || '').trim().toLowerCase();
      const isContact =
        target.hasAttribute('data-meta-contact') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        /(?:wa\.me|api\.whatsapp\.com|web\.whatsapp\.com)/i.test(href) ||
        /\b(?:support|whatsapp|call|email)\b/.test(label);

      if (isContact) trackMetaPixel('Contact', { contact_method: href.split(':')[0] || 'support_cta' });
    };

    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [pathname, privateJourney]);

  if (privateJourney || pathname === '/rupiqa/partner/register' || pathname.startsWith('/rupiqa/p/') || process.env.NEXT_PUBLIC_META_PIXEL_ENABLED_RUPIQA !== 'true' || !PIXEL_ID) return null;

  return (
    <noscript>
      <img
        alt=""
        height="1"
        width="1"
        style={{ display: 'none' }}
        src={`https://www.facebook.com/tr?id=${encodeURIComponent(PIXEL_ID)}&ev=PageView&noscript=1`}
      />
    </noscript>
  );
}
